import React, {Component} from "react";
import * as Yup from "yup";
import _ from "lodash";
import {TransitionGroup, CSSTransition} from "react-transition-group";

import FormWrapper from "./FormWrapper";
import FormGrid, {FormGridField} from "./FormGrid";

import "./FormSet.scss";
import {FormUtils} from "..";
import {uuidv4} from "../../utils";
import {useSelector} from "react-redux";
import {configurationsStoreSelectors} from "../../../modules/configurations/store/store";
import {subsidiaryDenominations} from "../../addressUtils";

export interface SubForm {
    type?: string;
    initialValues: any;
    fields: FormGridField[];
    validation: Yup.Schema<any>;
    key: string;
    hideDelete?: boolean;
    open?: boolean;
    subFormLabel?: string;
    onChange: (...args: any) => any;
    onDelete?: (...args: any) => any;
    //isActive: boolean;
}

export interface SubFormDefinition {
    fields: FormGridField[];
    formType: string;
    emptyValue?: any;
    addLabel: string;
    disableAdd?: boolean;
    hideDelete?: boolean;
    subFormLabel?: string;
    presets?: any[];
    extraFields?: any;
    subsidiary?: any;
    gameInstances?: any[];
}

export interface FormSetProps {
    subFormTypes: SubFormDefinition[];
    initialValues: any[];
    onChange: (...args: any) => any;
    onDelete?: (...args: any) => any;
    formSetLabel?: string;
    hideAdd?: boolean;
    isConfig?: boolean;
    presets?: any[];
    extraFields?: any;
    subsidiary?: any;
    generalError?: string;
    gameInstances?: any[];
}

export interface FormSetState {
    subForms: SubForm[];
    subFormsValues: any[];
}

const generateValidation = subFormType => {
    return Yup.object().shape(
        subFormType.fields.reduce((accumulator, field) => {
            return {
                ...accumulator,
                [field.name]: field.validationSchema
                    ? FormUtils.generateYupValidatorFromSchema(
                        field.validationSchema
                    )
                    : field.validation
            };
        }, {})
    );
};

class FormSet extends Component<FormSetProps, FormSetState> {
    constructor(props) {
        super(props);

        this.state = this.props.initialValues
            ? {
                subForms: this.props.initialValues.map(value =>
                    this.createSubForm(value)
                ),
                subFormsValues: this.props.initialValues.map(value => {
                    return {
                        values: value,
                        isValid: false
                    };
                })
            }
            : {
                subForms: [],
                subFormsValues: []
            };
    }

    componentWillUnmount = () => {
        this.setState = (_state, _callback) => {
            return;
        };
    };

    createSubForm(value): SubForm {
        let subFormType = value.type
            ? this.props.subFormTypes.find(
                subFormType => subFormType.formType === value.type
            )
            : null;

        if (!subFormType) subFormType = this.props.subFormTypes[0];

        return {
            type: value.type,
            fields: subFormType.fields,
            initialValues: value,
            validation: generateValidation(subFormType),
            key: _.uniqueId(),
            subFormLabel: subFormType.subFormLabel,
            open: true,
            hideDelete: subFormType.hideDelete,
            onChange: this.onChange
        };
    }

    getTypeByValues = values => {
        let subFormType = values.type
            ? this.props.subFormTypes.find(
                subFormType => subFormType.formType === values.type
            )
            : null;
        if (!subFormType) subFormType = this.props.subFormTypes[0];
        return subFormType.formType;
    };

    toogleExpandSubForm = index => {
        let newSubFormsValues = [...this.state.subFormsValues];
        let newSubForms = [...this.state.subForms];
        if (newSubFormsValues[index].values.hasOwnProperty("open")) {
            newSubFormsValues[index].values.open =
                !newSubFormsValues[index].values.open;
            this.setState(
                {
                    subFormsValues: newSubFormsValues
                },
                () => {
                    this.fireChange();
                }
            );
        } else {
            newSubForms[index].open = !newSubForms[index].open;
            this.setState({
                subForms: newSubForms
            });
        }
    };

    onAdd = index => {
        let newSubFormValue =
            this.props.subFormTypes[index] &&
            this.props.subFormTypes[index].emptyValue
                ? {
                    values: _.cloneDeep(
                        this.props.subFormTypes[index].emptyValue
                    ),
                    isValid: false
                }
                : {values: {}, isValid: false};

        if (newSubFormValue.values.hasOwnProperty('uuid')) {
            newSubFormValue.values.uuid = uuidv4()
        }

        if (newSubFormValue.values.hasOwnProperty('prizes')) {
            newSubFormValue.values.prizes.map(prize => {
                if (prize.hasOwnProperty('uuid')) {
                    prize.uuid = uuidv4();
                }
                return prize;
            })
        }
        let newSubForm = this.createSubForm(newSubFormValue.values);

        const newSubForms: SubForm[] = [newSubForm, ...this.state.subForms];
        const newSubFormValues = [
            newSubFormValue,
            ...this.state.subFormsValues
        ];

        this.setState(
            {
                subForms: [...newSubForms],
                subFormsValues: [...newSubFormValues]
            },
            () => {
                this.fireChange();
            }
        );
    };

    onChange = (index, value) => {
        const newSubFormValues = [...this.state.subFormsValues];
        const newSubForms = [...this.state.subForms];
        newSubFormValues[index] = {...value};

        if (value.values.hasOwnProperty("type") && (value.values.type === "proportional" || value.values.type === "normal" || this.hasSpecialPrize(index))) {
            newSubForms.map(form => {
                const field = form.fields.find(field => field.name === "id")
                if (field) {
                    field.validationSchema.unique = newSubFormValues.map(formValue => formValue.values.id)
                    form.validation = generateValidation(form)
                }
            })
            newSubFormValues[index].isValid = newSubForms[index].validation.isValidSync(newSubFormValues[index].values);
        }

        if (newSubFormValues[index].values.hasOwnProperty("prize_index")) {
            newSubFormValues[index].isValid = newSubForms[index].validation.isValidSync(newSubFormValues[index].values);
        }

        if (!_.isEqual(newSubFormValues, this.state.subFormsValues)) {
            this.setState(
                {
                    subFormsValues: newSubFormValues,
                    subForms: newSubForms
                },
                () => {
                    this.fireChange();
                }
            );
        }
    };

    onDelete = index => {
        if (this.props.onDelete) {
            this.props.onDelete(this.deleteGroupAtIndex, index);
        } else {
            this.deleteGroupAtIndex(index);
        }
    };

    deleteGroupAtIndex = index => {
        const newSubForms = [...this.state.subForms];
        newSubForms.splice(index, 1);
        const newSubFormsValues = [...this.state.subFormsValues];
        newSubFormsValues.splice(index, 1);

        if (this.hasProgressiveField(index)) {
            newSubForms.map(form => {
                const field = form.fields.find(field => field.name === "id")
                if (field) {
                    field.validationSchema.unique = newSubFormsValues.map(formValue => formValue.values.id)
                    form.validation = generateValidation(form)
                }
            })
        }

        this.setState(
            {
                subForms: [...newSubForms],
                subFormsValues: [...newSubFormsValues]
            },
            () => {
                this.fireChange();
            }
        );
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        return {
            ...prevState,
            subForms: prevState.subForms.map(subForm => {
                let subFormType = subForm.type
                    ? nextProps.subFormTypes.find(
                        subFormType => subFormType.formType === subForm.type
                    )
                    : nextProps.subFormTypes.find(
                        subFormType =>
                            subFormType.formType ===
                            subForm.initialValues.type
                    );

                if (!subFormType) {
                    subFormType = nextProps.subFormTypes[0];
                }

                let newFields = [...subForm.fields];

                if (
                    FormUtils.areFieldsDifferent(
                        subForm.fields,
                        subFormType.fields
                    )
                ) {
                    newFields = [...subFormType.fields];
                }

                if (
                    nextProps.subFormTypes[0].formType == "gameGroup" ||
                    nextProps.subFormTypes[0].formType == "terminalGroup"
                ) {
                    return {
                        ...subForm,
                        fields: [...subFormType.fields],
                        validation: generateValidation(
                            nextProps.subFormTypes[0]
                        )
                    };
                }

                return {
                    ...subForm,
                    fields: newFields
                };
            })
        };
    }

    fireChange() {
        this.props.onChange({
            values: this.state.subFormsValues.map(v => v.values),
            isValid: this.state.subFormsValues.every(v => v.isValid)
        });
    }

    renderAddButtons() {
        return this.props.subFormTypes.map((subFormType, index) => {
            return subFormType.disableAdd || this.props.hideAdd ? null : (
                <button
                    type="button"
                    className="red-btn add-button"
                    key={"add-button" + index}
                    onClick={() => this.onAdd(index)}
                >
                    <i className="icon-plus-light"></i>
                    {subFormType.addLabel}
                </button>
            );
        });
    }

    isEditingConfiguration() {
        const editingConfiguration = useSelector(
            configurationsStoreSelectors.editingConfiguration
        );
        return editingConfiguration.created_on
    }

    hasUniqueField(index) {
        return (
            this.getTypeByValues(this.state.subFormsValues[index].values) == "gameGroup" ||
            this.getTypeByValues(this.state.subFormsValues[index].values) == "terminalGroup" ||
            this.hasProgressiveField(index) ||
            this.hasSpecialPrize(index)
        )
    }

    hasProgressiveField(index) {
        return (
            this.getTypeByValues(this.state.subFormsValues[index].values) == "fractionated" ||
            this.getTypeByValues(this.state.subFormsValues[index].values) == "proportional" ||
            this.getTypeByValues(this.state.subFormsValues[index].values) == "normal" ||
            this.getTypeByValues(this.state.subFormsValues[index].values) == "levels" ||
            this.getTypeByValues(this.state.subFormsValues[index].values) == "multipots" ||
            this.getTypeByValues(this.state.subFormsValues[index].values) == "jumbolink" ||
            this.getTypeByValues(this.state.subFormsValues[index].values) == "luckytokenslink" ||
            this.getTypeByValues(this.state.subFormsValues[index].values) == "reelstrike" ||
            this.getTypeByValues(this.state.subFormsValues[index].values) == "easylink" ||
            this.getTypeByValues(this.state.subFormsValues[index].values) == "sap")
    }

    hasSpecialPrize(index) {
        return (
            this.getTypeByValues(this.state.subFormsValues[index].values) == "mrChampion" ||
            this.getTypeByValues(this.state.subFormsValues[index].values) == "pigBoom")
    }


    searchPreset(preset){
        //so for necessario fazer alguma coisa com o valor selecionado
    }


    renderSubForms(generalError) {

        const subForms = _.cloneDeep(this.state.subForms);

        return (
            <TransitionGroup>
                {
                    subForms.map((subForm, index) => {
                        // If is a prize
                        if (
                            this.state.subFormsValues[index].values.hasOwnProperty(
                                "proportion_type"
                            )
                        ) {
                            // If value for proportion_type is not set
                            /*if (
                                this.state.subFormsValues[index].values[
                                    "proportion_type"
                                ] == "" ||
                                this.state.subFormsValues[index].values[
                                    "proportion_type"
                                ] == undefined
                            ) {*/
                            // remove max and min bet fields
                            subForm.fields = subForm.fields.filter(
                                field =>
                                    field.name != "max_bet" &&
                                    field.name != "min_bet"
                            );
                            /*} else if (
                                this.state.subFormsValues[index].values[
                                    "proportion_type"
                                ] == "proportional"
                            ) {
                                // remove min bet field
                                subForm.fields = subForm.fields.filter(
                                    field => field.name != "min_bet"
                                );
                            } else if (
                                this.state.subFormsValues[index].values[
                                    "proportion_type"
                                ] == "normal"
                            ) {
                                // remove max bet field
                                subForm.fields = subForm.fields.filter(
                                    field => field.name != "max_bet"
                                );
                            }*/
                        }

                        //In edit mode, filter presets by individual pot for sap, easylink and reelstrike progressives.
                        subForm.fields = _.cloneDeep(this.state.subForms[index].fields)
                        if (this.isEditingConfiguration && (subForm.type === 'sap' || subForm.type === 'easylink' || subForm.type === 'luckytokenslink' || subForm.type === 'reelstrike') && subForm.fields.find(field => field.name === 'preset')) {
                            const field = subForm.fields.find(field => field.name === 'preset');
                            const indexField = subForm.fields.findIndex(field => field.name === 'preset');
                            if ("items" in field.fieldOptions) {
                                const selectPreset = field.fieldOptions.items.find(preset => preset.value === subForm.initialValues.preset);
                                if (selectPreset)
                                    subForm.fields[indexField].fieldOptions["items"] = field.fieldOptions.items.filter(item => {
                                        return !!item.individual_pot === !!selectPreset.individual_pot
                                    });
                            }
                        }

                        //hide the individual_pot field in the reelstrike progressive when it is not the Mega and Grand prize
                        if (subForm.fields[0].name === "prize_index" &&
                            subForm.fields[0].fieldOptions.noValueLabel === 'reelstrike' &&
                            subForm.initialValues.prize_index !== 0 &&
                            subForm.initialValues.prize_index !== 1) {
                            let index = subForm.fields.findIndex(f => f.name === 'individual_pot')
                            if (index > -1) {
                                subForm.fields[index].columnClass = 'hidden'
                            }
                        }

                        if (subForm.fields.find(field => field.name === 'preset')) {
                            const indexFieldChange = subForm.fields.findIndex(field => field.name === 'preset');
                            if (this.isEditingConfiguration &&
                                (subForm.type === 'multipots' || subForm.type === 'jumbolink') &&
                                subForm.fields[indexFieldChange].fieldOptions["items"].find(i => i.value === 0)) {
                                if (subForm.initialValues.preset === 0) {
                                    subForm.fields[indexFieldChange].fieldOptions.disabled = true
                                } else {
                                    const i = subForm.fields[indexFieldChange].fieldOptions["items"].findIndex(i => i.value === 0)
                                    subForm.fields[indexFieldChange].fieldOptions["items"].splice(i, 1)
                                }
                            }

                            subForm.fields[indexFieldChange].fieldOptions["items"].map(i => {
                                    // i.value é o id do preset
                                    i.label = '(' + i.value + ') ' + i.label
                                    if (i.certified) i.label = '[Certified] ' + i.label
                                }
                            )
                            subForm.fields[indexFieldChange].fieldOptions["searchAction"] = this.searchPreset
                        }

                        return (
                            <CSSTransition
                                timeout={800}
                                appear={
                                    /*subForm.open*/ this.state.subFormsValues[
                                        index
                                        ].values.hasOwnProperty("open")
                                        ? false
                                        : true
                                }
                                className="sub-form"
                                data-key={subForm.key}
                                key={subForm.key}
                            >
                                <div>
                                    <div className="form-wrapper">
                                        <div
                                            className={`form-wrapper-header ${
                                                //subForm.open ? "open" : ""
                                                this.state.subFormsValues[
                                                    index
                                                    ].values.hasOwnProperty("open")
                                                    ? this.state.subFormsValues[
                                                        index
                                                        ].values.open
                                                        ? "open"
                                                        : ""
                                                    : subForm.open
                                                        ? "open"
                                                        : ""
                                            }`}
                                        >
                                            <div
                                                className="clickable-header"
                                                onClick={() =>
                                                    this.toogleExpandSubForm(index)
                                                }
                                            >
                                                <i className="icon-caret-down-solid"/>
                                                {this.state.subFormsValues[
                                                        index
                                                        ].values.hasOwnProperty("id") &&
                                                    !this.state.subFormsValues[
                                                        index
                                                        ].values.hasOwnProperty(
                                                        "level_title"
                                                    ) &&
                                                    this.state.subFormsValues[index]
                                                        .values.type !==
                                                    "progressive_group" &&
                                                    this.state.subFormsValues[index]
                                                        .values.type !==
                                                    "progressive_group_levels" &&
                                                    this.props.subFormTypes[0]
                                                        .formType !==
                                                    "gameSpecificSettings" && (
                                                        <p>
                                                            {
                                                                this.state
                                                                    .subFormsValues[
                                                                    index
                                                                    ].values.id
                                                            }
                                                        </p>
                                                    )}
                                                {this.state.subFormsValues[
                                                    index
                                                    ].values.hasOwnProperty(
                                                    "level_title"
                                                ) && (
                                                    <p>
                                                        {
                                                            this.state
                                                                .subFormsValues[
                                                                index
                                                                ].values.level_title
                                                        }
                                                    </p>
                                                )}
                                                {(this.state.subFormsValues[index]
                                                        .values.type ==
                                                    "progressive_group" ||
                                                    this.state.subFormsValues[index]
                                                        .values.type ==
                                                    "progressive_group_levels") && (
                                                    <p>Prog. Group</p>
                                                )}
                                                {this.props.subFormTypes[0]
                                                        .formType ==
                                                    "gameSpecificSettings" && (
                                                        <p> {
                                                            this.state
                                                                .subFormsValues[
                                                                index
                                                                ].values.name
                                                        }
                                                        </p>
                                                    )}
                                                {subForm.subFormLabel && (
                                                    <div className="subform-label">
                                                        {subForm.subFormLabel}
                                                    </div>
                                                )}
                                            </div>

                                            {!subForm.hideDelete && (
                                                <div
                                                    className="delete-entry"
                                                    onClick={() =>
                                                        this.onDelete(index)
                                                    }
                                                >
                                                    <i className="icon-trash-alt-light"/>
                                                </div>
                                            )}
                                        </div>
                                        {generalError && <div className="input-error" style={{
                                            margin: '10px 20px'
                                        }}>
                                            <span className="input-error-message">{generalError}</span>
                                        </div>}
                                        <FormWrapper
                                            key={`wrapper ${subForm.key}-${this.state.subFormsValues[
                                                index
                                                ].values.hasOwnProperty("key")
                                                ? this.state.subFormsValues[
                                                    index
                                                    ].values.key
                                                : ""
                                            }`}
                                            initialValues={
                                                this.state.subFormsValues[index]
                                                    .values
                                            }
                                            hasUniqueField={this.hasUniqueField(index)}
                                            validation={subForm.validation}
                                            onChange={value =>
                                                this.onChange(index, value)
                                            }
                                            fields={subForm.fields}
                                        >
                                            <div className="row collapse-container">
                                                <FormGrid fields={subForm.fields} isConfig={this.props.isConfig}/>
                                            </div>
                                        </FormWrapper>
                                        {subForm.open &&
                                            this.props.subsidiary
                                            &&
                                            this.state.subFormsValues[index].values.hasOwnProperty("fractionated_jackpot_list")
                                            &&
                                            this.state.subFormsValues[index].values["fractionated_jackpot_list"].length > 0 && (
                                                <div className="array-input input-group">
                                                    <div
                                                        className={`input ${this.getTypeByValues(this.state.subFormsValues[index].values) == "fractionated" ? "fractionated-jackpot-list" : ""} `}
                                                        style={{height: "auto"}}>
                                                        <label className="label">Fractionated Jackpot List</label>

                                                        <table className="array-table" cellSpacing="0" cellPadding="0">
                                                            <thead>
                                                            <tr>
                                                                <th key={'thbet'}>Bet</th>
                                                                {Object.keys(this.state.subFormsValues[index].values["fractionated_jackpot_list"][0]).map((v, v_index) => {
                                                                    if (v !== 'bet') return (
                                                                        <th key={'th' + v_index}>{subsidiaryDenominations[this.props.subsidiary.toUpperCase()]((parseInt(v) / 100).toString())}</th>)
                                                                })}
                                                            </tr>
                                                            </thead>

                                                            <tbody>
                                                            {this.state.subFormsValues[index].values["fractionated_jackpot_list"].map((value, index) => {
                                                                    return (
                                                                        <tr key={'tr' + index}>
                                                                            <td key={'tdbet'}>{value['bet']}</td>
                                                                            {Object.keys(value).map((v, v_index) => {
                                                                                if (v !== 'bet') return (
                                                                                    <td key={'td' + v_index}>{value[v]}</td>)
                                                                            })}
                                                                        </tr>
                                                                    )
                                                                }
                                                            )
                                                            }
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            </CSSTransition>
                        );
                    })}
            </TransitionGroup>
        );
    }

    render() {
        let showSubForms = true;
        if (
            this.props.initialValues.length > 0 &&
            "key" in this.props.initialValues[0] &&
            this.props.initialValues[0].key === 0
        ) {
            showSubForms = false;
        }
        return (
            <div className="form-set">
                {this.props.formSetLabel && showSubForms && (
                    <label className="label">{this.props.formSetLabel}</label>
                )}
                {this.renderAddButtons()}
                {showSubForms && this.renderSubForms(this.props.generalError)}
            </div>
        );
    }
}

export default FormSet;